import {ActionTree, GetterTree, Module, MutationTree} from "vuex"
import {RootState} from "@/store/RootStore"
import {make} from "vuex-pathify"
import {Order, OrderLineItem, SpaceBooking, Waiver} from "rp-components"
import { StoreUtils } from "@/utils/StoreUtils"
import Services from "@/services/Services"

export const namespaced: boolean = true

export interface OrderState {
    order?: Order
    requiredWaivers: Waiver[]
    orderedBookings: SpaceBooking[]
}

export const state: OrderState = {
    order: undefined,
    requiredWaivers: [],
    orderedBookings: []
}

//===================================
// Getters
//===================================
export const getters: GetterTree<OrderState, RootState> = {}

//===================================
// Actions
//===================================
export const actions: ActionTree<OrderState, RootState> = {
    ...make.actions(state),

    async getOrder({ dispatch }, orderId: string) {
        await StoreUtils.withLoading(dispatch, async () => {
            const order = await Services.orders.getOrder(orderId)
            dispatch("setOrder", order)
        })
    },

    async abandonOrder({ dispatch }, orderId: string) {
        await StoreUtils.withLoading(dispatch, async () => {
            await Services.orders.abandonOrder(orderId)
            dispatch("setOrder", undefined)
        })
    },

    async submitOrder({ dispatch }, orderId: string) {
        await StoreUtils.withLoading(dispatch, async () => {
            const order = await Services.orders.submitOrder(orderId)
            dispatch("setOrder", order)
        })
    },

    async fetchRequiredWaivers({ dispatch }, entityIds: string[]) {
        await StoreUtils.withLoading(dispatch, async () => {
            const waivers = await Promise.all(entityIds.map(
                entityId => Services.orders.getRequiredWaiver(entityId)
            ))
            dispatch("setRequiredWaivers", waivers.flat())
        })
    },

    async consentToWaiver({ dispatch }, waiver: Waiver) {
        return await StoreUtils.withLoading(dispatch, async () => {
            await Services.orders.consentToWaiver(waiver)
        })
    },

    async fetchOrderedBookings({ dispatch }, payload: { propertyId: number, lineItems: OrderLineItem[]} ) {
        const propertyId = payload.propertyId
        const lineItems = payload.lineItems
        return await StoreUtils.withLoading(dispatch, async () => {
            const orderedBookings = await Promise.all(lineItems.map(lineItem => {
                const spaceId = lineItem.paymentProviderMetadata["spaceId"]
                return Services.spaces.getBooking(propertyId, spaceId, lineItem.entityId)
            }))
            await dispatch("setOrderedBookings", orderedBookings)
        })
    }
}

//===================================
// Mutations
//===================================
export const mutations: MutationTree<OrderState> = {
    ...make.mutations(state)
}

//===================================
// Module
//===================================
export const order: Module<OrderState, RootState> = {
    namespaced,
    state: state,
    getters: getters,
    actions: actions,
    mutations: mutations
}


